import { useCallback, useEffect, useState, type ReactNode } from "react";
import { AppShell } from "./components/AppShell";
import { OverviewPage } from "./pages/Overview";
import { TenantsPage } from "./pages/Tenants";
import { CopyNetworkPage } from "./pages/CopyNetwork";
import { LeadersPage } from "./pages/Leaders";
import { FollowersPage } from "./pages/Followers";
import { RiskControlsPage } from "./pages/RiskControls";
import { BridgeStatusPage } from "./pages/BridgeStatus";
import { AuditTrailPage } from "./pages/AuditTrail";
import { SupportOpsPage } from "./pages/SupportOps";
import { SettingsPage } from "./pages/Settings";
import { LoginPage, SessionLoadingPage, UnauthorizedPage } from "./pages/LoginPage";
import { SetupPage } from "./pages/SetupPage";
import { NotFoundPage } from "./pages/NotFound";
import { StatusPage } from "./pages/StatusPage";
import {
  logoutSession,
  restoreAuthenticatedSession,
  setMemoryCsrfToken,
  type AuthState,
} from "./lib/auth";

function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname || "/";
}

function resolvePath(pathname: string): string {
  const path = normalizePath(pathname);
  if (path === "/") return "/dashboard/overview";
  if (path === "/dashboard") return "/dashboard/overview";
  if (path === "/admin") return "/dashboard/overview";
  return path;
}

function isDashboardPath(path: string): boolean {
  return path === "/dashboard" || path.startsWith("/dashboard/");
}

export default function App() {
  const [pathname, setPathname] = useState(() => resolvePath(window.location.pathname));
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });

  const refreshSession = useCallback(async () => {
    try {
      const result = await restoreAuthenticatedSession();
      if (!result.ok) {
        setMemoryCsrfToken(null);
        setAuth({ status: "anonymous" });
        return;
      }
      setAuth({
        status: "authenticated",
        user: result.user,
        session: result.session,
        csrfToken: result.csrfToken,
      });
    } catch {
      setMemoryCsrfToken(null);
      setAuth({ status: "anonymous" });
    }
  }, []);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    const onPop = () => setPathname(resolvePath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    const desired = resolvePath(window.location.pathname);
    if (desired !== window.location.pathname) {
      window.history.replaceState({}, "", desired);
      setPathname(desired);
    }
  }, []);

  const navigate = (path: string) => {
    const next = resolvePath(path);
    if (next !== window.location.pathname) {
      window.history.pushState({}, "", next);
    }
    setPathname(next);
  };

  const onLogout = async () => {
    await logoutSession();
    setAuth({ status: "anonymous" });
    navigate("/login");
  };

  if (auth.status === "loading") {
    return (
      <AppShell pathname="/login" onNavigate={navigate}>
        <SessionLoadingPage />
      </AppShell>
    );
  }

  if (pathname === "/login") {
    return (
      <AppShell pathname={pathname} onNavigate={navigate}>
        <LoginPage
          onNavigate={navigate}
          onAuthenticated={(next) => {
            setAuth({
              status: "authenticated",
              user: next.user,
              session: next.session,
              csrfToken: next.csrfToken,
            });
            navigate("/dashboard/overview");
          }}
        />
      </AppShell>
    );
  }

  if (pathname === "/setup") {
    return (
      <AppShell pathname={pathname} onNavigate={navigate}>
        <SetupPage
          onNavigate={navigate}
          onAuthenticated={(next) => {
            setAuth({
              status: "authenticated",
              user: next.user,
              session: next.session,
              csrfToken: next.csrfToken,
            });
            navigate("/dashboard/overview");
          }}
        />
      </AppShell>
    );
  }

  if (pathname === "/status") {
    return (
      <AppShell pathname={pathname} onNavigate={navigate}>
        <StatusPage onNavigate={navigate} />
      </AppShell>
    );
  }

  // Fail closed: dashboard requires an MFA-assured authenticated session.
  if (isDashboardPath(pathname) && auth.status !== "authenticated") {
    return (
      <AppShell pathname="/login" onNavigate={navigate}>
        <UnauthorizedPage onNavigate={navigate} />
      </AppShell>
    );
  }

  let page: ReactNode;
  switch (pathname) {
    case "/dashboard/overview":
      page = <OverviewPage />;
      break;
    case "/dashboard/tenants":
      page = <TenantsPage />;
      break;
    case "/dashboard/copy-network":
      page = <CopyNetworkPage />;
      break;
    case "/dashboard/leaders":
      page = <LeadersPage />;
      break;
    case "/dashboard/followers":
      page = <FollowersPage />;
      break;
    case "/dashboard/risk-controls":
      page = <RiskControlsPage />;
      break;
    case "/dashboard/bridge":
      page = <BridgeStatusPage />;
      break;
    case "/dashboard/audit":
      page = <AuditTrailPage />;
      break;
    case "/dashboard/support":
      page = <SupportOpsPage />;
      break;
    case "/dashboard/settings":
      page = <SettingsPage />;
      break;
    default:
      page = <NotFoundPage onNavigate={navigate} />;
  }

  return (
    <AppShell
      pathname={pathname}
      onNavigate={navigate}
      onLogout={auth.status === "authenticated" ? onLogout : undefined}
      sessionEmail={auth.status === "authenticated" ? auth.user.email : undefined}
    >
      {page}
    </AppShell>
  );
}

